import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, LayoutGrid, Play, Shuffle } from "lucide-react";
import { DESTINATIONS, itemsFor, offlineReady, pickSurprise } from "@/data/travelCatalog";
import { pushRecent } from "@/lib/travelMode";
import { playCue } from "@/lib/soundCues";
import { track } from "@/lib/track";

// ONE Travel world-card system, shared by Travel Mode (/travel) and the child homepage (/).
export const bandOf = (ageGroupId) => (ageGroupId === "little" ? "4-6" : ageGroupId === "older" ? "11-15" : "7-10");

// Learning worlds shown on the homepage only — existing hubs, same card system. No counts (none in a shared catalogue), honest one-line blurbs instead.
export const LEARNING_WORLDS = [
  { key: "lessons", title: "Lessons", sub: "Every subject, your year", to: "/subjects", art: "/generated/learn-lessons.webp", tint: "mwq-card-tint-primary" },
  { key: "reading", title: "Reading", sub: "Books, passages & phonics", to: "/reading", art: "/generated/learn-reading.webp", tint: "mwq-card-tint-reward" },
  { key: "maths", title: "Maths", sub: "Maths Academy", to: "/maths-academy", art: "/generated/learn-maths.webp", tint: "mwq-card-tint-success" },
  { key: "words", title: "Spelling & Words", sub: "Spelling Bee & word games", to: "/spelling-bee", art: "/generated/learn-words.webp", tint: "mwq-card-tint-reward" },
  { key: "languages", title: "Languages", sub: "Hello in 12+ languages", to: "/languages", art: "/generated/learn-languages.webp", tint: "mwq-card-tint-creative" },
  { key: "ask", title: "Ask the Globe", sub: "Curious? Ask a question", to: "/ask", art: "/generated/learn-ask.webp", tint: "mwq-card-tint-primary" },
];

function WorldCard({ to, art, tint, title, meta, eager, young, testId, countTestId }) {
  return (
    <li className="min-w-0">
      <Link to={to} data-testid={testId} onClick={() => playCue("travelTap")} aria-label={`Open ${title}`}
        className={`mwq-card ${tint} h-full p-2.5 md:p-3 flex flex-col gap-2 focus-visible:ring-4 focus-visible:ring-sky-300 overflow-hidden`}>
        <img src={art} alt="" width={512} height={512} loading={eager ? "eager" : "lazy"} decoding="async" className="travel-dest-art w-full object-cover rounded-xl" />
        <span className="font-heading font-bold leading-tight text-[1.05rem] min-[400px]:text-[1.15rem] md:text-[1.35rem] min-[1100px]:text-[1.2rem] break-words" style={young ? { fontSize: "1.45rem" } : undefined}>{title}</span>
        <span className="mwq-meta -mt-1" data-testid={countTestId}>{meta}</span>
        <span className="mwq-btn-primary inline-flex items-center justify-center gap-1.5 mt-auto h-11 md:h-12 text-sm md:text-base w-full rounded-xl"><Play size={16} strokeWidth={3} aria-hidden /> Open</span>
      </Link>
    </li>
  );
}

// Deterministic daily shuffle: same order all day on a device, a fresh order tomorrow.
export const daySeed = () => new Date().toISOString().slice(0, 10);
function seededShuffle(list, seed) {
  let h = 0; for (const ch of String(seed)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const rnd = () => { h = (h * 1664525 + 1013904223) >>> 0; return h / 4294967296; };
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [out[i], out[j]] = [out[j], out[i]]; }
  return out;
}

export function WorldsGrid({ base = "/travel", filter = (x) => x, online = true, young = false, prefix = "travel", className = "", extra = [], only = null, seed = null, cols = "grid-cols-2 min-[720px]:grid-cols-3 min-[1100px]:grid-cols-6" }) {
  const travelCards = DESTINATIONS.filter((d) => !only || only.includes(d.key)).map((d) => {
    const n = d.key === "mine" ? null : filter(itemsFor(d.key)).length;
    const off = d.key === "mine" ? null : offlineReady(filter(itemsFor(d.key))).length;
    if (n === 0) return null;
    const meta = n != null ? `${n} activities${!online && off ? ` · ${off} offline` : ""}` : "Your saved stuff";
    return { key: d.key, to: `${base}/${d.key}`, art: d.art, tint: d.tint, title: d.title, meta, eager: d.key === "play" || d.key === "explore", testId: `${prefix}-dest-${d.key}`, countTestId: `${prefix}-count-${d.key}` };
  }).filter(Boolean);
  const learnCards = extra.map((w) => ({ key: w.key, to: w.to, art: w.art, tint: w.tint, title: w.title, meta: w.sub, testId: `${prefix}-learn-${w.key}` }));
  let cards = [...travelCards, ...learnCards];
  if (seed) cards = seededShuffle(cards, seed).map((c, i) => ({ ...c, eager: i < 2 }));
  return (
    <ul className={`grid gap-3 md:gap-4 ${cols} ${className}`} data-testid={`${prefix}-destinations`}>
      {cards.map(({ key, ...c }) => <WorldCard key={key} {...c} young={young} />)}
    </ul>
  );
}

/** Surprise Me — the ONE random pick (pickSurprise) used by Travel Mode and the homepage. */
export function useSurpriseMe({ online, band, source = "travel", pool = null }) {
  const navigate = useNavigate();
  return () => {
    const pick = pool ? pool[Math.floor(Math.random() * pool.length)] : pickSurprise(online, band);
    if (!pick) return;
    playCue("travelTap"); pushRecent(pick.id); track("travel_surprise", { id: pick.id, offline: !online, source }); navigate(pick.to);
  };
}

export function QuickStart({ base = "/travel", onSurprise, favCount = 0, subtitle, prefix = "travel", className = ""}) {
  return (
    <section className={`mwq-card p-3 md:p-4 bg-white/70 ${className}`} data-testid={`${prefix}-surprise-block`}>
      <button type="button" onClick={onSurprise} data-testid={`${prefix}-surprise`}
        className="mwq-btn-primary w-full h-14 md:h-[68px] rounded-2xl inline-flex items-center justify-center gap-2.5 text-xl md:text-2xl font-heading font-bold shadow-[0_6px_18px_rgba(47,99,238,0.28)] active:translate-y-px active:shadow-none focus-visible:ring-4 focus-visible:ring-sky-300">
        <Shuffle size={24} strokeWidth={3} aria-hidden /> Surprise Me
      </button>
      <p className="mt-2 text-center text-xs md:text-sm font-bold text-[var(--color-text-muted)]" data-testid={`${prefix}-surprise-sub`}>{subtitle}</p>
      <div className="mt-3 grid grid-cols-1 min-[360px]:grid-cols-2 gap-2 md:gap-3" data-testid={`${prefix}-utility`}>
        <Link to={`${base}/all`} data-testid={`${prefix}-all`} className="mwq-btn-secondary inline-flex items-center justify-center gap-1.5 h-11 md:h-12 px-2 text-sm md:text-base rounded-xl border-2 whitespace-nowrap min-w-0"><LayoutGrid size={18} aria-hidden className="shrink-0" /> <span className="truncate">All Activities</span></Link>
        <Link to={`${base}/favourites`} data-testid={`${prefix}-favourites`} className="mwq-btn-secondary inline-flex items-center justify-center gap-1.5 h-11 md:h-12 px-2 text-sm md:text-base rounded-xl border-2 whitespace-nowrap min-w-0"><Heart size={18} aria-hidden className={`shrink-0 ${favCount ? "fill-rose-500 text-rose-600" : ""}`} /> <span className="truncate">Favourites{favCount ? ` (${favCount})` : ""}</span></Link>
      </div>
    </section>
  );
}

/** Continue Playing — real recent activity only (travelMode.getRecent); callers hide it when empty. */
export function ContinueRow({ items, prefix = "travel", className = "" }) {
  if (!items?.length) return null;
  return (
    <section className={className} data-testid={`${prefix}-continue`}>
      <h2 className="mwq-card-title">Continue Playing</h2>
      <ul className="mt-2 flex gap-3 overflow-x-auto snap-x pb-1 -mx-4 px-4 [scrollbar-width:thin]">
        {items.slice(0, 4).map((it) => (
          <li key={it.id} className="snap-start shrink-0 w-[11.5rem]">
            <Link to={it.to} onClick={() => { playCue("travelTap"); pushRecent(it.id); }} data-testid={`${prefix}-continue-${it.id}`}
              className="mwq-card p-2.5 flex items-center gap-2.5 min-h-[64px] focus-visible:ring-4 focus-visible:ring-sky-300">
              <img src={it.art} alt="" width={44} height={44} loading="lazy" decoding="async" className="h-11 w-11 rounded-lg object-cover shrink-0" />
              <span className="font-bold text-sm leading-tight line-clamp-2">{it.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
